import { useMemo } from 'react';

import { useUser } from 'context/user';
import { READ_ONLY_CONTRACTOR_COMPANY_IDS } from 'lib/whitelistConstants';

/**
 * Hook to check if the current user is a contractor with read-only access.
 * Contractors from companies in the READ_ONLY_CONTRACTOR_COMPANY_IDS list
 * have read-only access to the platform.
 *
 * They cannot:
 * - Edit their profile
 * - Add/edit/delete withdrawal methods
 * - Withdraw funds
 * - Create/edit/delete invoices
 * - Request or modify invoices
 *
 * They CAN:
 * - Set up 2FA (security settings)
 * - View their profile, invoices, withdrawal history, etc.
 *
 * To add a new company to the read-only list, add their company ID to
 * READ_ONLY_CONTRACTOR_COMPANY_IDS in lib/whitelistConstants.ts
 *
 * @returns Object containing:
 *  - isReadOnlyContractor: boolean indicating if the user is a read-only contractor
 *  - readOnlyTooltipMessage: standard tooltip message for disabled actions
 */
export const useReadOnlyContractor = () => {
  const { currentCompany, roleType } = useUser() || {};

  const isReadOnlyContractor = useMemo(() => {
    // Only restrict contractors (workers), not admins
    if (roleType !== 'worker') {
      return false;
    }
    // Check if the contractor's company is in the read-only list
    return READ_ONLY_CONTRACTOR_COMPANY_IDS.includes(currentCompany?.companyID);
  }, [currentCompany?.companyID, roleType]);

  // Standard tooltip message for disabled actions
  const readOnlyTooltipMessage = 'This action is not available for your account';

  return {
    isReadOnlyContractor,
    readOnlyTooltipMessage,
    // Alias for backward compatibility with existing code
    isOceansContractor: isReadOnlyContractor,
  };
};

// Default export for convenience
export default useReadOnlyContractor;

// Named export with original name for backward compatibility
export const useOceansContractorReadOnly = useReadOnlyContractor;
